import { Layer, LayerProps } from 'shared/map-exports';
import { getWatchdutyTileUrl } from 'components/Map/layers/getWatchdutyTileUrl';
import VisibilityToggledSource from 'components/Map/layers/VisibilityToggledSource';
import { useMemo } from 'react';
import { DataDrivenPropertyValueSpecification } from 'mapbox-gl';
import { useQueryParams } from 'hooks/useQueryParms';
import { useProgressionModel } from 'features/wildfires/hooks/useProgressionModel';
import {
  PROGRESSION_MODEL_DEFAULT_COLOR,
  PROGRESSION_MODEL_FILL_COLORS,
} from 'features/wildfires/wildfire.constants';
import { BEFORE_LAYER_ID } from 'components/Map/styles/constants';

type ProgressionModelLayerProps = {
  visible: boolean;
  modelId: number;
};

type LayerPropsInput = {
  modelId: number;
  /** timestamp in ascending order */
  timeStampsFillColor: [timestamp: number, color: string][];
  cutoffTimestamp: number;
};

const LAYER_NAME = 'fire_progression_models';
const SOURCE_ID = 'progression_models';
const LAYER_ID = `${SOURCE_ID}_layer`;

const createTimestampColorExpression = (
  /** timestamp in ascending order */
  timeStampsFillColor: [number, string][],
): DataDrivenPropertyValueSpecification<string> => {
  const conditions = [];
  const timestampGetter = ['get', 'timestamp'];

  // First condition (≤ first timestamp)
  conditions.push(['<=', timestampGetter, timeStampsFillColor[0][0]]);
  conditions.push(timeStampsFillColor[0][1]);

  // Rest of conditions (between timestamps)
  for (let i = 1; i < timeStampsFillColor.length; i += 1) {
    conditions.push([
      'all',
      ['>', timestampGetter, timeStampsFillColor[i - 1][0]],
      ['<=', timestampGetter, timeStampsFillColor[i][0]],
    ]);
    conditions.push(timeStampsFillColor[i][1]);
  }

  return ['case', ...conditions, PROGRESSION_MODEL_DEFAULT_COLOR];
};

const getLayerStyle = (input: LayerPropsInput): LayerProps => {
  const { modelId, timeStampsFillColor, cutoffTimestamp } = input;
  return {
    type: 'fill',
    'source-layer': LAYER_NAME,
    paint: {
      'fill-color': createTimestampColorExpression(timeStampsFillColor),
      'fill-opacity': 0.25,
    },
    filter: [
      'all',
      ['==', ['get', 'model_run_id'], modelId],
      ['<=', ['get', 'timestamp'], ['literal', cutoffTimestamp]],
    ],
    layout: {
      'fill-sort-key': ['/', 1, ['get', 'timestamp']],
    },
  };
};

export const ProgressionModelLayer = (
  props: ProgressionModelLayerProps,
): JSX.Element | null => {
  const { visible, modelId } = props;
  const [{ index }] = useQueryParams<{ index: string }>();
  const selectedIndex = parseInt(index ?? '0', 10);
  const { modelDateRange } = useProgressionModel({ modelId });

  const cutoffTimestamp = modelDateRange[selectedIndex].timestamp;

  const timeStampsFillColor: [timestamp: number, color: string][] = useMemo(
    () =>
      modelDateRange.map((modelDate, idx) => [
        modelDate.timestamp,
        PROGRESSION_MODEL_FILL_COLORS[idx] || PROGRESSION_MODEL_DEFAULT_COLOR,
      ]),
    [modelDateRange],
  );

  return (
    <VisibilityToggledSource
      id={SOURCE_ID}
      visible={visible}
      url={getWatchdutyTileUrl(LAYER_NAME, {
        model_run_id: modelId.toString(),
      })}
    >
      <Layer
        id={LAYER_ID}
        {...getLayerStyle({ modelId, timeStampsFillColor, cutoffTimestamp })}
        beforeId={BEFORE_LAYER_ID}
      />
    </VisibilityToggledSource>
  );
};
